import * as Headless from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import backend from '../../services/backend';
import SortableTable from '../../components/SortableTable';
import StatusIndicator from '../../components/StatusIndicator';
import { PeekLink } from '../../components/PeekLink';
import { TableSkeleton } from '../../components/Skeleton';
import { DocumentThumbnailHover } from './DocumentThumbnailHover';

interface DocumentTypeDocumentsDrawerProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  type: { classification: string; documentCount: number; pageCount: number } | null;
}

export function DocumentTypeDocumentsDrawer({ open, onClose, projectId, type }: DocumentTypeDocumentsDrawerProps) {
  const classification = type?.classification ?? '';

  const documentsQuery = useQuery({
    queryKey: ['getProjectDocumentTypeDocuments', projectId, classification],
    queryFn: () => backend.getProjectDocumentTypeDocuments(projectId, classification).then((res) => res.data),
    enabled: open && classification.length > 0,
  });

  const documents = documentsQuery.data?.documents ?? [];

  return (
    <Headless.Dialog open={open} onClose={onClose} className="relative z-50">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-950/25 transition duration-200 data-closed:opacity-0 dark:bg-zinc-950/50"
      />
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-y-0 right-0 flex max-w-full">
          <Headless.DialogPanel
            transition
            className="flex w-screen max-w-2xl transform flex-col bg-white shadow-xl transition duration-200 ease-in-out data-closed:translate-x-full dark:border-l dark:border-zinc-700 dark:bg-zinc-900"
          >
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4 dark:border-zinc-700">
              <Headless.DialogTitle className="text-base font-medium text-gray-900 dark:text-zinc-100">
                {type ? `${type.classification} · ${type.documentCount} docs · ${type.pageCount} pages` : ''}
              </Headless.DialogTitle>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-zinc-400 dark:hover:text-zinc-200"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {documentsQuery.isLoading ? (
                <TableSkeleton />
              ) : documentsQuery.error ? (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {(documentsQuery.error as Error).message}
                </p>
              ) : documents.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-zinc-400">
                  No reconstructed documents for this type.
                </p>
              ) : (
                <SortableTable
                  storageId="project-document-type-documents"
                  columns={[
                    {
                      accessor: 'suggestedFilename',
                      label: 'Document',
                      sortable: true,
                      render: (doc) => (
                        <DocumentThumbnailHover pageId={doc.firstPageId}>
                          <PeekLink
                            type="reconstructed"
                            id={doc.reconstructedDocumentId}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            {doc.suggestedFilename}
                          </PeekLink>
                        </DocumentThumbnailHover>
                      ),
                    },
                    {
                      accessor: 'pageCount',
                      label: 'Pages',
                      sortable: true,
                      render: (doc) => (
                        <span className="block text-right tabular-nums">{doc.pageCount}</span>
                      ),
                    },
                    {
                      accessor: 'classificationCertainty',
                      label: 'Certainty',
                      sortable: true,
                      render: (doc) => (
                        <span className="tabular-nums">{Math.round(doc.classificationCertainty * 100)}%</span>
                      ),
                    },
                    {
                      accessor: 'hasExtraction',
                      label: 'Extraction',
                      sortable: true,
                      render: (doc) => (
                        <StatusIndicator
                          title={doc.hasExtraction ? 'Extracted' : 'Pending'}
                          status={doc.hasExtraction ? 'done' : 'pending'}
                        />
                      ),
                    },
                  ]}
                  data={documents}
                  rowKey={(doc) => doc.reconstructedDocumentId}
                />
              )}
            </div>
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
}
